import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useClickAway, useMount } from 'react-use';
import {
  ApiGeoUnitWithDayConstraints,
  ApiGetGeoUnitRulesRequestFromJSON,
  ApiGetGeoUnitRulesResponse,
  ApiGetGeoUnitsRequestFromJSON,
  ApiItemType
} from '@ibe/api';
import { LoggerFactory } from '@ibe/services';
import { faCalendar, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, CustomInput, Form, Label, Row } from 'reactstrap';
import fallback from '../../../_Translations/generated/camper-search.en.t.json';
import Keys from '../../../_Translations/generated/camper-search.en.t.json.keys';
import useConfig from '../../Hooks/useConfig';
import useTranslation from '../../Hooks/useTranslation';
import { CamperSearchParams, validateCamperSearch } from './DefaultCamperSearchSchema';
import useApi from '../../Hooks/useApi';
import NestedGeoUnitsSelect from '../../Form/formComponents/NestedGeoUnitsSelect';
import { DateRangePicker } from '../PackageSearch/SearchComponents';
import {
  camperFillWithDefaults,
  DateConstraintsReturn,
  getDateConstraints,
  getRelatedGeoUnitRules
} from './externals';
import {
  TravelerSelection,
  TravelerSelectionDropdown
} from '../../Form/formComponents/TravelerSelect';
import { facPeople } from '../../../Styles/customIcons';
import { WeekDayConstraints } from '../../Components/Input/DateRangeInput/DateRangeInputProps';

const logger = LoggerFactory.get('CamperSearch');

export interface CamperSearchProps {
  searchParams?: Partial<CamperSearchParams>;
  onSubmit: (params: CamperSearchParams) => void;
  setPickupGeoUnits?: (geoUnits: ApiGeoUnitWithDayConstraints[]) => void;
}

export const CamperSearch = (props: CamperSearchProps): JSX.Element => {
  const { t } = useTranslation('camper-search', fallback, Keys);
  const { searchParams, onSubmit, setPickupGeoUnits } = props;
  const config = useConfig();
  const api = useApi();

  const [geoUnits, setGeoUnits] = useState<ApiGeoUnitWithDayConstraints[]>([]);
  const [relatedGeoUnitRules, setRelatedGeoUnitRules] = useState<
    ApiGetGeoUnitRulesResponse | undefined
  >(undefined);

  useMount(async () => {
    try {
      const newGeoUnits = Object.entries(
        (
          await api.getGeoUnits(
            ApiGetGeoUnitsRequestFromJSON({
              type: 'GetGeoUnitsRequest',
              itemType: ApiItemType.CAMPER,
              typeCodes: ['COUNTRY']
            })
          )
        ).geoUnits
      ).map(([, geoUnit]) => geoUnit as ApiGeoUnitWithDayConstraints);

      setGeoUnits(newGeoUnits);
      if (setPickupGeoUnits) {
        setPickupGeoUnits(newGeoUnits);
      }
    } catch (err) {
      logger.error(err);
    }
  });

  const values = useMemo(() => {
    return camperFillWithDefaults(searchParams || {}, config, geoUnits);
  }, [searchParams, geoUnits]);

  const [pickupStation, setPickupStation] = useState<ApiGeoUnitWithDayConstraints | undefined>(
    values.pickupStationGeoUnit
  );
  const [dropOffStation, setDropOffStation] = useState<ApiGeoUnitWithDayConstraints | undefined>(
    values.dropOffStationGeoUnit
  );
  const [pickupDate, setPickupDate] = useState<string | undefined>(values.startDate);
  const [dropOffDate, setDropOffDate] = useState<string | undefined>(values.endDate);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(true);
  const [adults, setAdults] = useState<number>(2);
  const [children, setChildren] = useState<number>(0);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const initialPickupChangeDone = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const handleAdultChange = (increment: boolean) => {
    setAdults(prev => (increment ? prev + 1 : prev - 1 > 0 ? prev - 1 : 1));
  };

  const handleChildrenChange = (increment: boolean) => {
    setChildren(prev => (increment ? prev + 1 : prev - 1 >= 0 ? prev - 1 : 0));
  };

  useEffect(() => {
    if (values.pickupStationGeoUnit) {
      setPickupStation(values.pickupStationGeoUnit);
    }
    if (values.dropOffStationGeoUnit) {
      setDropOffStation(values.dropOffStationGeoUnit);
    }
    if (values.startDate) {
      setPickupDate(values.startDate);
    }
    if (values.endDate) {
      setDropOffDate(values.endDate);
    }
    if (values.adults) {
      setAdults(parseInt(values.adults) || 2);
    }
    if (values.children) {
      setChildren(parseInt(values.children) || 0);
    }
    setIsChecked(
      values.pickupStationGeoUnit?.code === values.dropOffStationGeoUnit?.code ||
        values.pickupStation === values.dropOffStation
    );
  }, [values]);

  const dateConstraints:
    | Record<
        'pickup' | 'dropoff',
        {
          minDate?: Date;
          maxDate?: Date;
          minDateRange?: number;
          weekDayConstraints?: WeekDayConstraints[];
          [key: string]: DateConstraintsReturn;
        }
      >
    | undefined = useMemo(
    () =>
      getDateConstraints(isChecked, pickupStation, dropOffStation, relatedGeoUnitRules, pickupDate),
    [pickupStation, dropOffStation, relatedGeoUnitRules, pickupDate, isChecked]
  );

  const getValues = useCallback((): CamperSearchParams => {
    return {
      startDate: pickupDate,
      endDate: dropOffDate,
      dropOffStationGeoUnit: dropOffStation,
      dropOffStationParentGeoUnit: geoUnits.find(
        geoUnit =>
          geoUnit.children && Object.keys(geoUnit.children).includes(dropOffStation?.id || '')
      ),
      dropOffStation: dropOffStation ? dropOffStation.code : '',
      pickupStationGeoUnit: pickupStation,
      pickupStationParentGeoUnit: geoUnits.find(
        geoUnit =>
          geoUnit.children && Object.keys(geoUnit.children).includes(pickupStation?.id || '')
      ),
      pickupStation: pickupStation ? pickupStation.code : '',
      adults: adults.toString(),
      children: children.toString()
    };
  }, [pickupDate, config, dropOffStation, dropOffDate, pickupStation, geoUnits, adults, children]);

  useEffect(() => {
    validateCamperSearch(getValues()).then((errors: {}) => {
      setIsFormValid(Object.keys(errors).length === 0);
    });
  }, [getValues]);

  useEffect(() => {
    if (isChecked) {
      setDropOffStation(pickupStation);
    }
  }, [isChecked]);

  const resetDates = (): void => {
    setPickupDate(undefined);
    setDropOffDate(undefined);
  };

  const handlePickupChange = async (
    station: ApiGeoUnitWithDayConstraints,
    ignoreReset = false
  ): Promise<void> => {
    setPickupStation(station);
    if (!ignoreReset) {
      resetDates();
    }
    if (isChecked) {
      setDropOffStation(station);
    }
    try {
      const geoUnitRules = !!station.id
        ? await api.getGeoUnitRules(
            ApiGetGeoUnitRulesRequestFromJSON({
              geoUnitId: station.id
            })
          )
        : {};
      const newRelatedGeoUnitRules = getRelatedGeoUnitRules(geoUnitRules, station, geoUnits);
      setRelatedGeoUnitRules({
        ...newRelatedGeoUnitRules
      });
      if (
        !!dropOffStation &&
        !newRelatedGeoUnitRules?.destinations?.find(dest =>
          Object.entries(dest.children).find(
            ([, destChild]) => destChild.code === dropOffStation.code
          )
        ) &&
        !isChecked
      ) {
        setDropOffStation(undefined);
      }
    } catch (err) {
      logger.error(err);
    }
  };

  const handleDropOffChange = (station: ApiGeoUnitWithDayConstraints): void => {
    setDropOffStation(station);
    resetDates();
  };

  const handleCheckboxChange = (): void => {
    setIsChecked(value => !value);
    resetDates();
  };

  useEffect(() => {
    if (geoUnits?.length > 0 && !!pickupStation && !initialPickupChangeDone.current) {
      handlePickupChange(pickupStation, true);
      initialPickupChangeDone.current = true;
    }
  }, [geoUnits, pickupStation]);

  const handleDateRangeChange = (start?: string, end?: string): void => {
    setPickupDate(!start || start === 'Invalid Date' ? '' : start);
    setDropOffDate(!end || end === 'Invalid Date' ? '' : end);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    onSubmit(getValues());
  };

  const isFromDate = (): boolean => {
    return (!pickupDate && !dropOffDate) || (!!pickupDate && !!dropOffDate);
  };

  const getDateConstraint = (property: string): DateConstraintsReturn => {
    if (isFromDate()) {
      return dateConstraints?.pickup[property] || undefined;
    } else {
      return dateConstraints?.dropoff[property] || undefined;
    }
  };

  const minDay = new Date();
  minDay.setDate(minDay.getDate() + config.datePickerMinDateOffset);

  useClickAway(containerRef, (): void => {
    setDropdownOpen(false);
  });

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="iso__service-search d-flex align-items-end">
        <Col
          sm={12}
          md={isChecked ? 8 : 4}
          className={isChecked ? 'camper__search__pickup xxxl-4' : 'camper__search__pickup xxxl-2'}
        >
          {geoUnits.length > 0 && (
            <>
              <div
                className={`camper__search__icon--dropoff${
                  isChecked ? ' camper__search__icon--double' : ''
                }`}
              >
                <FontAwesomeIcon icon={faArrowRight} color={'var(--secondary)'} />
                {isChecked && <FontAwesomeIcon icon={faArrowLeft} color={'var(--secondary)'} />}
              </div>
              <NestedGeoUnitsSelect
                options={geoUnits}
                onChange={handlePickupChange}
                getValue={(dA): string => dA.code}
                getLabel={(dA): string => dA.name}
                selectedOption={pickupStation}
                placeholder={t(Keys.pickupStation)}
              />
            </>
          )}
        </Col>
        {!isChecked && (
          <Col sm={12} md={4} xl={4} className="camper__search__dropoff xxxl-2 mt-3 mt-md-0">
            <div className="camper__search__icon--dropoff">
              <FontAwesomeIcon icon={faArrowLeft} color={'var(--secondary)'} />
            </div>
            <NestedGeoUnitsSelect
              options={relatedGeoUnitRules?.destinations || []}
              isDisabled={!relatedGeoUnitRules?.destinations}
              onChange={handleDropOffChange}
              getValue={(dA): string => dA.code}
              getLabel={(dA): string => dA.name}
              selectedOption={dropOffStation}
              placeholder={t(Keys.dropOffStation)}
            />
          </Col>
        )}
        <Col sm={12} md={4} xl={4} className="camper__search__checkbox xxxl-2">
          <Label className="d-flex align-items-center mb-0">
            <CustomInput
              id="iso__camper__search__checkbox__pickup-dropoff"
              type="checkbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
            />
            <span>{t(Keys.dropoffIsPickup)}</span>
          </Label>
        </Col>
        <Col sm={12} md={12} xl={8} className="camper__search__date-range xxxl-4">
          {dropdownOpen && (
            <div ref={containerRef}>
              <TravelerSelectionDropdown
                ref={containerRef}
                adults={adults}
                children={children}
                handleChildrenChange={handleChildrenChange}
                handleAdultChange={handleAdultChange}
              />
            </div>
          )}
          <div className="camper__search__date-range-container">
            <div className="camper__search__icon">
              <FontAwesomeIcon icon={faCalendar} />
            </div>
            <DateRangePicker
              customDateFormat
              withDateRangePicker
              onDateRangeChange={handleDateRangeChange}
              startDate={pickupDate}
              endDate={dropOffDate}
              updateStateFromProps
              disabled={!pickupStation || !dropOffStation || !relatedGeoUnitRules}
              minDate={getDateConstraint('minDate') as Date}
              maxDate={getDateConstraint('maxDate') as Date}
              minDateRangeSpan={
                !isFromDate()
                  ? (getDateConstraint('minDateRange') as number | undefined)
                  : undefined
              }
              weekDayConstraints={
                getDateConstraint('weekDayConstraints') as WeekDayConstraints[] | undefined
              }
              ignoreStartDateForFilter
              selectsDisabledDaysInRange
              useAdvancedFeatures
            />
          </div>
          <div className="traveler-selection-container">
            <div className="traveler-selection-icon">
              <FontAwesomeIcon viewBox={'2 6 12 12'} icon={facPeople} color={'var(--secondary)'} />
            </div>
            <TravelerSelection
              toggleDropdown={toggleDropdown}
              adults={adults}
              children={children}
            />
          </div>
        </Col>
        <Col xl={4} className="camper__search__submit-btn xxxl-2 mt-3">
          <Button disabled={!isFormValid} color="secondary" block type="submit">
            {t(Keys.submit)}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
